/**
 * Feature flags: every switchable feature, declared once. An environment sets them as
 * `FEATURE_<NAME>=on|off` variables in its pipeline variable group; the pipeline passes the same
 * values to the API (app settings, through Bicep) and to the client build. A flag that isn't set
 * takes its default, or off when a flag it needs is off (or one it `excludes` is on). A flag with
 * `offRequires` can only be off while those flags are on. Keep `requires`, `excludes` and `offRequires`
 * pointing at flags listed earlier. To add one: an entry here and a row in the README's "Feature flags" table.
 */
export const FEATURES = {
  accounts: {
    env: "FEATURE_ACCOUNTS",
    default: true,
    description: "Sign-in, the account pages and order history",
  },
  staffTools: {
    env: "FEATURE_STAFF_TOOLS",
    default: true,
    description: "The admin pages: order fulfillment and contractor pricing",
    requires: ["accounts"],
  },
  contractorProgram: {
    env: "FEATURE_CONTRACTOR_PROGRAM",
    default: true,
    description: "Contractor accounts and job orders",
    requires: ["accounts"],
  },
  contractorPricing: {
    env: "FEATURE_CONTRACTOR_PRICING",
    default: true,
    description: "Special prices and a job order discount set by staff for each contractor",
    requires: ["contractorProgram", "staffTools"],
  },
  jobSiteDelivery: {
    env: "FEATURE_JOB_SITE_DELIVERY",
    default: true,
    description: "Delivering job orders to the job site",
    requires: ["contractorProgram"],
  },
  anonymousPickSheets: {
    env: "FEATURE_ANONYMOUS_PICK_SHEETS",
    default: false,
    description: "Printed job order pick sheets show only the job's title and QR code: no items, nor who it's for",
    requires: ["contractorProgram", "staffTools"],
  },
  anonymousJobOrders: {
    env: "FEATURE_ANONYMOUS_JOB_ORDERS",
    default: false,
    description: "Contractors can keep a job order anonymous: its pick sheet shows only the title and QR code",
    requires: ["contractorProgram", "staffTools"],
    // With every pick sheet anonymous already, there's nothing to choose.
    excludes: ["anonymousPickSheets"],
  },
  storeLocations: {
    env: "FEATURE_STORE_LOCATIONS",
    default: false,
    description: "Where each product is in the store (like Aisle 14a), on its page and on staff pick lists",
  },
  metricsDigest: {
    env: "FEATURE_METRICS_DIGEST",
    default: true,
    description: "The weekly activity email (it also needs the digest settings)",
  },
  automaticTax: {
    env: "FEATURE_AUTOMATIC_TAX",
    default: false,
    description: "Stripe Tax at checkout (needs a head office and registrations in Stripe)",
  },
  payInStorePickup: {
    env: "FEATURE_PAY_IN_STORE_PICKUP",
    default: false,
    description: "Signed-in shoppers send their cart to the store for pickup without paying; staff ring it up there",
    requires: ["accounts", "staffTools"],
  },
  cardPayments: {
    env: "FEATURE_CARD_PAYMENTS",
    default: true,
    description: "Paying by card online (Stripe): checkout, and contractor deposits",
    // Without card payments, a cart can only go to the store as a pay-in-store pickup order.
    offRequires: ["payInStorePickup"],
  },
  contractorDeposits: {
    env: "FEATURE_CONTRACTOR_DEPOSITS",
    default: true,
    description:
      "Job orders take a deposit by card when they're placed; off, they're paid in full at pickup or delivery",
    requires: ["contractorProgram", "cardPayments"],
  },
  pickupOrderEmails: {
    env: "FEATURE_PICKUP_ORDER_EMAILS",
    default: false,
    description:
      "Shoppers can email themselves a pay-in-store pickup order with its QR code (it also needs SENDGRID_API_KEY and ORDER_EMAIL_FROM)",
    requires: ["payInStorePickup"],
  },
  contractorApproval: {
    env: "FEATURE_CONTRACTOR_APPROVAL",
    default: false,
    description:
      "Contractor accounts wait for an admin to activate them, and admins are emailed to review each new one (the email also needs SENDGRID_API_KEY and ORDER_EMAIL_FROM)",
    // Staff activate accounts on the admin contractor pages, there with this or contractor pricing.
    requires: ["contractorProgram", "staffTools"],
  },
  allContractorPricing: {
    env: "FEATURE_ALL_CONTRACTOR_PRICING",
    default: true,
    description: "Special prices and a job order discount for every contractor at once",
    // Anyone can turn on "I'm a contractor", so these prices wait for an admin's approval.
    requires: ["contractorPricing", "contractorApproval"],
  },
  // A button on the sign-in page for each identity provider (SIGN_IN_PROVIDERS in signIn.ts); each
  // also needs the provider added to the brand's Entra tenant (`npm run setup:entra`).
  signInMicrosoft: {
    env: "FEATURE_SIGN_IN_MICROSOFT",
    default: false,
    description: "Signing in with a personal Microsoft account (Outlook.com, Xbox, Skype)",
    requires: ["accounts"],
  },
  signInGoogle: {
    env: "FEATURE_SIGN_IN_GOOGLE",
    default: false,
    description: "Signing in with a Google account",
    requires: ["accounts"],
  },
  signInApple: {
    env: "FEATURE_SIGN_IN_APPLE",
    default: false,
    description: "Signing in with an Apple Account",
    requires: ["accounts"],
  },
  signInFacebook: {
    env: "FEATURE_SIGN_IN_FACEBOOK",
    default: false,
    description: "Signing in with a Facebook account",
    requires: ["accounts"],
  },
  signInLinkedIn: {
    env: "FEATURE_SIGN_IN_LINKEDIN",
    default: false,
    description: "Signing in with a LinkedIn account",
    requires: ["accounts"],
  },
} as const satisfies Record<string, FeatureDefinition>;

interface FeatureDefinition {
  env: `FEATURE_${string}`;
  default: boolean;
  description: string;
  requires?: readonly string[];
  /** Flags that must be off for this one to be on. */
  excludes?: readonly string[];
  /** Flags that must be on for this one to be off. */
  offRequires?: readonly string[];
}

export type FeatureKey = keyof typeof FEATURES;
export type Features = Readonly<Record<FeatureKey, boolean>>;

export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

/** The admin contractor pages (the list and each contractor's page) are there when any of these is on. */
export const CONTRACTOR_ADMIN_FEATURES = [
  "contractorPricing",
  "contractorApproval",
] as const satisfies readonly FeatureKey[];

/** Whether these flags give staff the admin contractor pages. */
export const hasContractorAdmin = (features: Features) => CONTRACTOR_ADMIN_FEATURES.some((f) => features[f]);

/** What every flag is with nothing set: its default, unless a flag it needs is off. */
export const DEFAULT_FEATURES: Features = Object.freeze(parseFeatures(() => undefined).features);

/** The defaults with some flags changed (for tests). */
export const withFeatures = (changes: Partial<Features>): Features => ({ ...DEFAULT_FEATURES, ...changes });

/**
 * Reads every flag through `read` (given the setting name, e.g. FEATURE_ACCOUNTS). Values are
 * "on" or "off" (any case). Unset, blank or "default" (how the setup script lists every flag in a
 * new variable group) takes the default, or off when a flag it needs is off or one it excludes is on,
 * so turning off accounts turns off what's built on them. Anything else, a flag set to "on"
 * while one it needs is off (or one it excludes is on), or a flag that ends up off while one in its
 * `offRequires` is off, is an error: the caller refuses to start (API) or build (client).
 */
export function parseFeatures(read: (name: string) => string | undefined): { features: Features; errors: string[] } {
  const errors: string[] = [];
  const features = {} as Record<FeatureKey, boolean>;
  // In registry order, so every flag's requirements are already decided.
  for (const key of FEATURE_KEYS) {
    const def: FeatureDefinition = FEATURES[key];
    const needs = (def.requires ?? []) as FeatureKey[];
    const missing = needs.find((n) => !features[n]);
    const clash = ((def.excludes ?? []) as FeatureKey[]).find((n) => features[n]);
    const raw = read(def.env)?.trim().toLowerCase();
    const value = raw === "default" ? undefined : raw;
    if (value && value !== "on" && value !== "off") {
      errors.push(`${def.env} must be "on" or "off", not "${value}".`);
    } else if (value === "on" && missing) {
      errors.push(`${def.env} is on, but it needs ${FEATURES[missing].env} on too.`);
    } else if (value === "on" && clash) {
      errors.push(`${def.env} is on, but it needs ${FEATURES[clash].env} off.`);
    }
    features[key] = missing || clash ? false : value === "on" || value === "off" ? value === "on" : def.default;
    const unmet = ((def.offRequires ?? []) as FeatureKey[]).find((n) => !features[n]);
    if (!features[key] && unmet) {
      errors.push(`${def.env} is off, but that needs ${FEATURES[unmet].env} on.`);
    }
  }
  return { features, errors };
}
